/**
 * The AI's view of a chat in the list (crm/src/public/chats/ai-layer.js pure
 * mappers): recency tier of the last message and the AI tag.
 */

const TIERS = [
  { key: 'live', maxMs: 4 * 60_000 },
  { key: 'active', maxMs: 15 * 60_000 },
  { key: 'cooling', maxMs: 60 * 60_000 },
  { key: 'idle', maxMs: 12 * 60 * 60_000 },
  { key: 'dormant', maxMs: Infinity },
] as const
export type RecencyTier = (typeof TIERS)[number]['key']

export function recencyTier(iso: string, now: Date): RecencyTier {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t) || t <= 0) return 'dormant'
  const age = Math.max(0, now.getTime() - t)
  return TIERS.find((x) => age <= x.maxMs)?.key ?? 'dormant'
}

export type AiTag = 'chats' | 'teases' | 'sexts' | 'ignores' | 'needs_you'

/**
 * The one AI tag on a chat row: what the AI is doing there. A hand-off or an
 * ignore wins; while the AI is chatting, its vibe says whether it teases
 * ("getting flirty") or sexts ("building to something"). Other statuses
 * (finished, paused) get no tag.
 */
export function aiTag(status: unknown, vibe: unknown): AiTag | null {
  const s = String(status ?? '').trim().toLowerCase()
  if (s === 'handed_off') return 'needs_you'
  if (s === 'ignored') return 'ignores'
  if (s !== 'active') return null
  const v = String(vibe ?? '').trim().toLowerCase()
  if (v === 'getting flirty') return 'teases'
  if (v === 'building to something') return 'sexts'
  return 'chats'
}

/** OnlyMonster buying power as 0–5 dots (chats.js getChatBuyingPowerLevel). */
export function buyingPowerLevel(value: unknown): number {
  if (value == null) return 0
  const n = Number(String(value).replace(/[^\d.]/g, ''))
  if (!Number.isFinite(n) || n <= 0) return 0
  if (n <= 5 && Number.isInteger(n)) return n
  if (n < 10) return 1
  if (n < 50) return 2
  if (n < 100) return 3
  if (n < 250) return 4
  return 5
}

/** Temperature 0–100 as one of four levels, or null without data. */
export function temperatureLevel(value: unknown): { score: number; level: 1 | 2 | 3 | 4 } | null {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  const score = Math.min(Math.max(n, 0), 100)
  return { score, level: score <= 25 ? 1 : score <= 50 ? 2 : score <= 75 ? 3 : 4 }
}
